// Fixed WebSocket Service - prevents connection errors
// This forces simulation mode and prevents real connection attempts

export type WebSocketEvent = 
  | 'dashboard:update'
  | 'booking:created'
  | 'booking:updated'
  | 'booking:assigned'
  | 'booking:completed'
  | 'booking:cancelled'
  | 'driver:status_changed'
  | 'driver:location_updated'
  | 'payment:updated'
  | 'assignment:changed'
  | 'notification:new'
  | 'system:alert';

export interface WebSocketMessage {
  event: WebSocketEvent;
  data: any;
  timestamp: string;
}

export interface ConnectionOptions {
  autoReconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
}

export type WebSocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error' | 'reconnecting';

class WebSocketService {
  private subscriptions: Map<WebSocketEvent, Set<(data: any) => void>> = new Map();
  private status: WebSocketStatus = 'disconnected';
  private options: ConnectionOptions;
  private mockDataInterval: NodeJS.Timeout | null = null;
  private statusCallbacks: Set<(status: WebSocketStatus) => void> = new Set();

  constructor() {
    this.options = {
      autoReconnect: true,
      reconnectInterval: 5000,
      maxReconnectAttempts: 10,
      heartbeatInterval: 30000,
    };

    console.log('🔌 WebSocketService: Initialized in SIMULATION mode (no real server)');
  }

  // ===== CONNECTION MANAGEMENT =====

  connect(token?: string): Promise<void> {
    return new Promise((resolve) => {
      if (this.status === 'connected') {
        console.log('🔌 WebSocketService: Already connected (simulated)');
        resolve();
        return;
      }

      console.log('🎭 WebSocketService: Starting simulation mode');
      this.setStatus('connecting');

      // Force simulation - no real connection attempts
      setTimeout(() => {
        this.setStatus('connected');
        this.startMockDataGeneration();
        console.log('✅ WebSocketService: Simulation connected');
        resolve();
      }, 1000);
    });
  }

  disconnect(): void {
    console.log('🔌 WebSocketService: Disconnecting simulation');
    this.stopMockDataGeneration();
    this.setStatus('disconnected');
    console.log('✅ WebSocketService: Simulation disconnected');
  }

  // ===== SUBSCRIPTION MANAGEMENT =====

  subscribe(event: WebSocketEvent, callback: (data: any) => void): () => void {
    if (!this.subscriptions.has(event)) {
      this.subscriptions.set(event, new Set());
    }
    this.subscriptions.get(event)!.add(callback);

    console.log(`🔔 WebSocketService: Subscribed to '${event}'`);

    // Return unsubscribe function
    return () => {
      this.subscriptions.get(event)?.delete(callback);
      console.log(`🔕 WebSocketService: Unsubscribed from '${event}'`);
    };
  }

  unsubscribe(event: WebSocketEvent, callback?: (data: any) => void): void {
    if (callback) {
      this.subscriptions.get(event)?.delete(callback);
    } else {
      this.subscriptions.delete(event);
    }
  }

  // ===== STATUS MANAGEMENT =====

  onStatusChange(callback: (status: WebSocketStatus) => void): () => void {
    this.statusCallbacks.add(callback);
    // Call immediately with current status
    callback(this.status);
    
    return () => {
      this.statusCallbacks.delete(callback);
    };
  }

  getStatus(): WebSocketStatus {
    return this.status;
  }

  private setStatus(status: WebSocketStatus): void {
    this.status = status;
    this.statusCallbacks.forEach(callback => callback(status));
  }

  // ===== MESSAGE SENDING =====

  send(event: WebSocketEvent, data: any): void {
    if (this.status !== 'connected') {
      console.warn('⚠️ WebSocketService: Cannot send - not connected');
      return;
    }

    console.log(`📤 WebSocketService: Sending '${event}'`, data);
    
    // In simulation mode, just log the message
    // In real implementation, this would send to server
  }

  // ===== SIMULATION =====

  private startMockDataGeneration(): void {
    if (this.mockDataInterval) return;

    console.log('🎭 WebSocketService: Starting mock data generation');
    
    this.mockDataInterval = setInterval(() => {
      this.generateMockEvents();
    }, 15000); // Generate events every 15 seconds
  }

  private stopMockDataGeneration(): void {
    if (this.mockDataInterval) {
      clearInterval(this.mockDataInterval);
      this.mockDataInterval = null;
      console.log('🎭 WebSocketService: Stopped mock data generation');
    }
  }

  private generateMockEvents(): void {
    const events: { event: WebSocketEvent; data: any }[] = [
      {
        event: 'dashboard:update',
        data: {
          activeBookings: Math.floor(Math.random() * 50) + 10,
          availableDrivers: Math.floor(Math.random() * 20) + 5,
          revenue: Math.floor(Math.random() * 10000) + 1000
        }
      },
      {
        event: 'driver:location_updated',
        data: {
          driverId: `driver-${Math.floor(Math.random() * 100)}`,
          location: {
            lat: 6.9271 + (Math.random() - 0.5) * 0.1,
            lng: 79.8612 + (Math.random() - 0.5) * 0.1
          },
          heading: Math.floor(Math.random() * 360),
          speed: Math.floor(Math.random() * 60)
        }
      },
      {
        event: 'booking:created',
        data: {
          id: `booking-${Date.now()}`,
          customerName: `Customer ${Math.floor(Math.random() * 1000)}`,
          from: 'Colombo Fort',
          to: 'Galle Face',
          status: 'pending'
        }
      }
    ];

    const randomEvent = events[Math.floor(Math.random() * events.length)];
    this.emit(randomEvent.event, randomEvent.data);
  }

  public emit(event: WebSocketEvent, data: any): void {
    const callbacks = this.subscriptions.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ WebSocketService: Error in callback for '${event}':`, error);
        }
      });
    }
  }
  public getActiveEvents(): WebSocketEvent[] {
    return Array.from(this.subscriptions.keys());
  }

  public subscribeToNotifications(callback: (data: any) => void): () => void {
    return this.subscribe('notification:new', callback);
  }

  // ===== UTILITY METHODS =====

  isConnected(): boolean {
    return this.status === 'connected';
  }

  getSubscriptionCount(): number {
    let total = 0;
    this.subscriptions.forEach(callbacks => {
      total += callbacks.size;
    });
    return total;
  }

  clearAllSubscriptions(): void {
    this.subscriptions.clear();
    console.log('🧹 WebSocketService: All subscriptions cleared');
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService;